import { motion } from "framer-motion";
import {
  type ActiveWordPosition,
  formatSegmentTime,
  type TranscriptionLine,
  type WordMatchRange,
} from "../lib/transcription";
import type {
  FlatMatch,
  SegmentMatchInfo,
} from "../hooks/useTranscriptSearch";
import { classNames } from "../utils";

/** Tailwind rounded-* class for a word inside a contiguous match pill so
 *  adjacent matched words render as one rounded shape instead of a string
 *  of disjoint pills. Non-match words round normally. */
function matchPillRadius(
  matchRange: WordMatchRange | undefined,
  wordIndex: number,
): string {
  if (!matchRange) return "rounded";
  if (matchRange.startWord === matchRange.endWord) return "rounded";
  if (wordIndex === matchRange.startWord) return "rounded-l";
  if (wordIndex === matchRange.endWord) return "rounded-r";
  return "";
}

export interface TranscriptLineProps {
  segmentIndex: number;
  lineIndex: number;
  line: TranscriptionLine;
  /** Active-word index *within the parent segment*, or -1 if no active word in this segment. */
  activeWordIndexInSegment: number;
  /** State of the active word — drives `trailing` opacity dimming. */
  activePosition: ActiveWordPosition | null;
  /** Tailwind class for the line's idle text color (drives the "this segment is the focus" gray hierarchy). */
  baseTextColor: string;
  isSearching: boolean;
  /** Match ranges + lookup set for the parent segment, or undefined when no matches. */
  segmentMatchInfo: SegmentMatchInfo | undefined;
  /** The globally-focused match (for nav prev/next styling), or null. */
  currentMatch: FlatMatch | null;
  /** Whether this line contains the globally-focused match. */
  isCurrentMatchLine: boolean;
  /** Bumped by jumpToCurrent — used to remount the active line and replay the ring animation. */
  popKey: number;
  onWordClick: (
    segmentIndex: number,
    wordIndex: number,
    timeSec: number,
  ) => void;
}

/**
 * One line of transcript inside a segment: leading timestamp + the
 * words that belong to that line, with active-word, match, and
 * trailing-word styling applied per-word.
 */
export function TranscriptLine({
  segmentIndex,
  lineIndex,
  line,
  activeWordIndexInSegment,
  activePosition,
  baseTextColor,
  isSearching,
  segmentMatchInfo,
  currentMatch,
  isCurrentMatchLine,
  popKey,
  onWordClick,
}: TranscriptLineProps) {
  const lineHasActiveWord =
    activeWordIndexInSegment >= line.startWordIndex &&
    activeWordIndexInSegment < line.startWordIndex + line.words.length;

  return (
    <motion.div
      // Re-key the active line on pop so framer-motion re-mounts and
      // replays the ring animation.
      key={lineHasActiveWord ? `pop-${popKey}` : "idle"}
      data-active-line={lineHasActiveWord || undefined}
      data-current-match={isCurrentMatchLine ? "true" : undefined}
      initial={false}
      animate={
        lineHasActiveWord && popKey > 0
          ? {
              boxShadow: [
                "0 0 0 0 rgba(101,38,219,0)",
                "0 0 0 6px rgba(101,38,219,0.35)",
                "0 0 0 0 rgba(101,38,219,0)",
              ],
            }
          : { boxShadow: "0 0 0 0 rgba(101,38,219,0)" }
      }
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="flex items-baseline gap-3 rounded"
    >
      <span className="w-9 shrink-0 select-none text-center text-xs text-[#71717A]">
        {formatSegmentTime(line.startMs)}
      </span>
      <p className={classNames("flex-1 text-sm leading-[1.6]", baseTextColor)}>
        {line.words.map((word, i) => {
          const wordIndex = line.startWordIndex + i;
          const isActive = wordIndex === activeWordIndexInSegment;
          // Find which match range (if any) this word is in, so we can
          // render contiguous matches as one continuous pill instead of
          // per-word disjoint pills.
          const matchRange = isSearching
            ? segmentMatchInfo?.ranges.find(
                (r) => wordIndex >= r.startWord && wordIndex <= r.endWord,
              )
            : undefined;
          const isMatch = !!matchRange;
          const isCurrentMatch =
            isMatch &&
            currentMatch?.segmentIndex === segmentIndex &&
            wordIndex >= currentMatch.startWord &&
            wordIndex <= currentMatch.endWord;
          const highlight = isCurrentMatch
            ? "current-match"
            : isMatch
              ? "match"
              : isActive
                ? activePosition!.state
                : null;
          const matchRadius = matchPillRadius(matchRange, wordIndex);
          // Include the trailing space INSIDE the span so the highlight
          // wraps cleanly without bleeding into the next word's territory.
          const text =
            i < line.words.length - 1 ? `${word.text} ` : word.text;
          return (
            <span
              key={i}
              onClick={() =>
                onWordClick(segmentIndex, wordIndex, word.start / 1000)
              }
              className={classNames(
                "cursor-pointer transition-colors",
                matchRadius,
                // Per Figma: all matches (current included) use the same
                // light-purple bg + saturated-purple text. Current is
                // differentiated by scroll position and the "N / M"
                // count, not color.
                highlight === "current-match" ||
                  highlight === "match" ||
                  highlight === "active"
                  ? "bg-[#F4F3FF] text-[#6526DB]"
                  : highlight === "trailing"
                    ? "bg-[#F4F3FF] text-[#6526DB] opacity-60"
                    : "hover:bg-[#F7F8F9]",
              )}
            >
              {text}
            </span>
          );
        })}
      </p>
    </motion.div>
  );
}
